import { supabase } from "@/lib/supabase/client";

// Inquiry insert type (simplified - matches database schema)
export interface InquiryInsert {
  name: string;
  email: string;
  phone?: string | null;
  organization?: string | null;
  category: string;
  source?: string;
  priority?: string;
  contextual_data?: Record<string, unknown> | null;
}

// Inquiry response type
export interface Inquiry extends InquiryInsert {
  id: string;
  status: string;
  created_at: string;
  updated_at?: string | null;
}

// Inquiry category labels for display
// Database only allows: 'join-commons', 'studio-project', 'labs-project', 'partnership', 'general'
export const INQUIRY_CATEGORIES = {
  "general": "General Inquiry",
  "studio-project": "Project Inquiry",
  "labs-project": "Labs Project",
  "join-commons": "Join Commons",
  "partnership": "Partnership Inquiry",
} as const;

/**
 * Submit a new inquiry to the database
 * Note: We don't use .select() because anon users don't have SELECT permission (RLS)
 */
export async function submitInquiry(data: InquiryInsert): Promise<{ success: boolean; error: Error | null }> {
  try {
    const insertData = {
      ...data,
      status: "new",
      source: data.source || "dugout-website",
      priority: data.priority || "normal",
      created_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from("inquiries")
      .insert([insertData]);

    if (error) throw error;

    // Trigger external service sync using the input data (no DB response needed)
    const inquiryForSync: Inquiry = {
      ...insertData,
      id: "", // Not available without SELECT, but webhooks can work without it
      updated_at: null,
    };
    syncToExternalServices(inquiryForSync).catch(console.error);

    return { success: true, error: null };
  } catch (error) {
    console.error("Error submitting inquiry:", error);
    // Log more details if it's a Supabase error
    if (error && typeof error === "object" && "message" in error) {
      console.error("Error message:", (error as { message: string }).message);
    }
    if (error && typeof error === "object" && "code" in error) {
      console.error("Error code:", (error as { code: string }).code);
    }
    if (error && typeof error === "object" && "details" in error) {
      console.error("Error details:", (error as { details: string }).details);
    }
    return { success: false, error: error as Error };
  }
}

/**
 * Sync inquiry to external services and send notifications
 */
async function syncToExternalServices(inquiry: Inquiry): Promise<void> {
  // Send email notification via Supabase Edge Function + Resend
  await sendEmailNotification(inquiry);

  // Sync to Notion via webhook (Zapier/Make)
  await syncToNotion(inquiry);

  // Sync to Google Sheets via webhook (optional - for spreadsheet logging only)
  await syncToGoogleSheets(inquiry);
}

/**
 * Sync inquiry to Notion
 */
async function syncToNotion(inquiry: Inquiry): Promise<void> {
  const NOTION_WEBHOOK_URL = import.meta.env.VITE_NOTION_WEBHOOK_URL;

  if (!NOTION_WEBHOOK_URL) {
    console.log("Notion webhook URL not configured, skipping sync");
    return;
  }

  try {
    // Map category to Notion format
    const categoryMap: Record<string, string> = {
      "general": "General",
      "studio-project": "Project",
      "labs-project": "Labs Project",
      "join-commons": "Join Commons",
      "partnership": "Partnership",
    };

    // Extract contextual data
    const contextualData = inquiry.contextual_data || {};
    const budgetRange = (contextualData.budget || "Not Specified") as string;

    // Build content sections
    const contentSections: string[] = [];
    if (contextualData.message) {
      contentSections.push(`## Message\n\n${contextualData.message}`);
    }
    if (Array.isArray(contextualData.interests) && contextualData.interests.length) {
      contentSections.push(`## Interests\n\n${contextualData.interests.join(", ")}`);
    }

    const payload = {
      inquiry_title: `${inquiry.name} - ${INQUIRY_CATEGORIES[inquiry.category as keyof typeof INQUIRY_CATEGORIES] || inquiry.category}`,
      category: categoryMap[inquiry.category] || "General",
      status: "New",
      priority: inquiry.priority || "Normal",
      email: inquiry.email,
      phone: inquiry.phone || "",
      organization: inquiry.organization || "",
      budget_range: budgetRange,
      source: "Dugout Website",
      submitted_date: inquiry.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
      notes: "Submitted via godugout.com contact form",
      page_content: contentSections.join("\n\n---\n\n"),
      raw_contextual_data: JSON.stringify(contextualData),
      supabase_id: inquiry.id,
    };

    const response = await fetch(NOTION_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      console.log("Successfully synced to Notion");
    } else {
      console.error("Failed to sync to Notion:", await response.text());
    }
  } catch (error) {
    console.error("Error syncing to Notion:", error);
  }
}

/**
 * Send email notification via Supabase Edge Function
 * Uses Resend API for reliable email delivery
 */
async function sendEmailNotification(inquiry: Inquiry): Promise<void> {
  try {
    const contextualData = inquiry.contextual_data || {};
    const flattenedData = Object.entries(contextualData).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value.join(", ") : String(value);
      return acc;
    }, {} as Record<string, string>);

    const categoryLabel = INQUIRY_CATEGORIES[inquiry.category as keyof typeof INQUIRY_CATEGORIES] || inquiry.category;

    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke("notify-dugout-inquiry", {
      body: {
        name: inquiry.name,
        email: inquiry.email,
        phone: inquiry.phone || undefined,
        organization: inquiry.organization || undefined,
        category: categoryLabel,
        message: flattenedData.message || undefined,
        source: inquiry.source || "dugout-website",
        timestamp: inquiry.created_at || new Date().toISOString(),
        website: flattenedData.website || undefined,
        referralSource: flattenedData.referralSource || flattenedData.howDidYouHear || undefined,
      },
    });

    if (error) {
      console.error("Edge function error:", error);
      return;
    }

    if (data?.success) {
      console.log("Email notification sent successfully via Resend");
    } else {
      console.error("Email notification failed:", data?.error);
    }
  } catch (error) {
    console.error("Error sending email notification:", error);
  }
}

/**
 * Sync inquiry to Google Sheets (optional - for spreadsheet logging)
 * Uses Google Apps Script webhook
 */
async function syncToGoogleSheets(inquiry: Inquiry): Promise<void> {
  const GOOGLE_SHEETS_WEBHOOK_URL = import.meta.env.VITE_GOOGLE_SHEETS_WEBHOOK_URL;

  if (!GOOGLE_SHEETS_WEBHOOK_URL) {
    console.log("Google Sheets webhook URL not configured, skipping sync");
    return;
  }

  try {
    const contextualData = inquiry.contextual_data || {};
    const flattenedData = Object.entries(contextualData).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value.join(", ") : String(value);
      return acc;
    }, {} as Record<string, string>);

    const categoryLabel = INQUIRY_CATEGORIES[inquiry.category as keyof typeof INQUIRY_CATEGORIES] || inquiry.category;

    const payload = {
      timestamp: inquiry.created_at || new Date().toISOString(),
      category: categoryLabel,
      name: inquiry.name,
      email: inquiry.email,
      phone: inquiry.phone || "",
      organization: inquiry.organization || "",
      source: inquiry.source || "dugout-website",
      status: inquiry.status || "new",
      message: flattenedData.message || "",
      ...flattenedData,
    };

    const response = await fetch(GOOGLE_SHEETS_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
      },
      body: JSON.stringify(payload),
      redirect: "follow",
    });

    if (response.ok) {
      console.log("Successfully synced to Google Sheets");
    } else {
      console.error("Failed to sync to Google Sheets:", await response.text());
    }
  } catch (error) {
    console.error("Error syncing to Google Sheets:", error);
  }
}
